import { useEventBus } from '@/composables/useEventBus';
import type { OnboardingStepDeps } from '@/composables/useOnboarding';
import i18n from '@/i18n';
import { createActionStep, createInfoStep } from '@/onboarding/stepStyles';
import type { StepOptions } from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import { nextTick } from 'vue';
import type { Composer } from 'vue-i18n';

const t = (key: string) => (i18n.global as unknown as Composer).t(key);

const eventBus = useEventBus();

const blockEvents = (e: Event) => {
  e.stopPropagation();
  // e.preventDefault(); // Optionnel, parfois nécessaire
};

// Liste des événements à bloquer pour empêcher PrimeVue de réagir
const eventsToBlock = ['click', 'mousedown', 'touchstart', 'pointerdown'];

export function getRecipeActionsSteps({ tour, waitForElement }: OnboardingStepDeps): StepOptions[] {
  return [
    // 12a - Détail de la recette créée
    createActionStep(
      {
        id: 'final-recipe-details',
        title: t('onboarding.chapters.recipeActions.steps.final-recipe-details.title'),
        text: t('onboarding.chapters.recipeActions.steps.final-recipe-details.text'),
        attachTo: { element: '.onboarding-recipe-actions', on: 'bottom' },
        canClickTarget: true,
        beforeShowPromise: async () => {
          await waitForElement('.onboarding-recipe-actions', 90000);
          await nextTick();
        },
        advanceOn: { selector: '.onboarding-recipe-actions', event: 'click' },
      },
      {
        placement: 'top',
      },
    ),

    // 12b - Valeurs nutritionnelles
    createInfoStep({
      id: 'nutrition-details',
      title: t('onboarding.chapters.recipeActions.steps.nutrition-details.title'),
      text: t('onboarding.chapters.recipeActions.steps.nutrition-details.text'),
      attachTo: { element: '.onboarding-menu-nutritional-values', on: 'bottom' },
      canClickTarget: false,

      when: {
        hide: () => {
          const overlay = document.querySelector('.shepherd-modal-overlay-container');
          if (overlay) {
            eventsToBlock.forEach((evt) => overlay.removeEventListener(evt, blockEvents, true));
          }
        },
      },

      beforeShowPromise: async () => {
        await nextTick();

        const overlay = document.querySelector('.shepherd-modal-overlay-container') || document.querySelector('.shepherd-modal-overlay');

        if (overlay) {
          eventsToBlock.forEach((evt) => overlay.addEventListener(evt, blockEvents, true));
          console.log('[Onboarding] Overlay locked against clicks/mousedown');
        }
      },

      buttons: [
        { text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true },
        {
          text: t('onboarding.common.next'),
          action: (e?: Event) => {
            // Sécurité pour le bouton Suivant
            if (e) {
              e.stopPropagation();
              e.preventDefault();
            }
            tour.value?.next();
          },
        },
      ],
    }),
    // 12c - Partager le visuel
    createInfoStep({
      id: 'share-recipe',
      title: t('onboarding.chapters.recipeActions.steps.share-recipe.title'),
      text: t('onboarding.chapters.recipeActions.steps.share-recipe.text'),
      attachTo: { element: '.onboarding-menu-share', on: 'top' },
      canClickTarget: false,

      // 1. NETTOYAGE : On retire les écouteurs quand l'étape se ferme ou avance
      when: {
        hide: () => {
          const overlay = document.querySelector('.shepherd-modal-overlay-container') || document.querySelector('.shepherd-modal-overlay');
          if (overlay) {
            eventsToBlock.forEach((evt) => overlay.removeEventListener(evt, blockEvents, true));
          }
        },
      },

      beforeShowPromise: async () => {
        const selector = '.onboarding-menu-share';

        const element = document.querySelector(selector);
        if (element) {
          const style = window.getComputedStyle(element);
          if (style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0') {
            console.log('[Onboarding] 12c: Item de menu déjà visible.');
            // On continue pour attacher le blocage
          } else {
            // Cas rare : élément présent mais caché
            const menuButton = document.querySelector('.onboarding-recipe-actions');
            if (menuButton) (menuButton as HTMLElement).click();
          }
        } else {
          // Ouverture du menu
          console.log('[Onboarding] 12c: Item non visible, ouverture du menu...');
          const menuButton = document.querySelector('.onboarding-recipe-actions');
          if (menuButton) {
            console.log('[Onboarding] 12c: Essai 1 - .click()');
            (menuButton as HTMLElement).click();
            await new Promise((resolve) => setTimeout(resolve, 300));

            let menuPanel = document.querySelector('[data-pc-name="menu"]');
            if (!menuPanel) {
              console.log('[Onboarding] 12c: Essai 2 - dispatchEvent(click)');
              menuButton.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
              await new Promise((resolve) => setTimeout(resolve, 300));
              menuPanel = document.querySelector('[data-pc-name="menu"]');
            }

            if (!menuPanel) {
              console.error("[Onboarding] 12c: Échec de l'ouverture du menu après 2 tentatives.");
              tour.value?.cancel();
              return Promise.reject("Échec de l'ouverture du menu");
            }
            console.log('[Onboarding] 12c: Panel de menu ouvert.');
          } else {
            console.error('[Onboarding] 12c: Bouton de menu introuvable.');
            tour.value?.cancel();
            return Promise.reject('Bouton de menu introuvable');
          }
        }

        // Polling
        await new Promise<void>((resolve, reject) => {
          const timeout = 5000;
          const interval = 100;
          let elapsedTime = 0;

          console.log(`[Onboarding] 12c: Démarrage du polling pour ${selector}...`);
          const poller = setInterval(() => {
            elapsedTime += interval;
            const el = document.querySelector(selector);
            if (el) {
              const s = window.getComputedStyle(el);
              if (s.display !== 'none' && s.visibility !== 'hidden' && s.opacity !== '0') {
                console.log(`[Onboarding] Polling 12c réussi: ${selector} trouvé !`);
                clearInterval(poller);
                resolve();
              }
            }
            if (elapsedTime >= timeout) {
              console.error(`[Onboarding] Polling 12c échoué: Timeout pour ${selector}`);
              clearInterval(poller);
              reject(new Error(`Timeout waiting for element: ${selector}`));
              tour.value?.cancel();
            }
          }, interval);
        });

        // 2. BLOCAGE TOTAL : On empêche mousedown ET click sur l'overlay
        await nextTick();
        const overlay = document.querySelector('.shepherd-modal-overlay-container') || document.querySelector('.shepherd-modal-overlay');
        if (overlay) {
          // 'true' (capture) est essentiel ici
          eventsToBlock.forEach((evt) => overlay.addEventListener(evt, blockEvents, true));
          console.log('[Onboarding] 12c: Overlay verrouillé contre la fermeture.');
        }
      },
      buttons: [
        {
          text: t('onboarding.common.previous'),
          action: (e?: Event) => {
            if (e) {
              e.stopPropagation();
              e.preventDefault();
            }
            tour.value?.back();
          },
          secondary: true,
        },
        {
          text: t('onboarding.common.next'),
          action: (e?: Event) => {
            if (e) {
              e.stopPropagation();
              e.preventDefault();
            }
            tour.value?.next();
          },
        },
      ],
    }),
    // 12d.1 - Modifier ingrédients
    createActionStep(
      {
        id: 'edit-ingredients',
        title: t('onboarding.chapters.recipeActions.steps.edit-ingredients.title'),
        text: t('onboarding.chapters.recipeActions.steps.edit-ingredients.text'),
        attachTo: { element: '.ingredients-list', on: 'top' },
        canClickTarget: true,
      },
      {
        placement: 'top',
      },
    ),
    // 12d.2 - Modifier un ingrédient
    createInfoStep({
      id: 'edit-ingredient-detail',
      title: t('onboarding.chapters.recipeActions.steps.edit-ingredient-detail.title'),
      text: t('onboarding.chapters.recipeActions.steps.edit-ingredient-detail.text'),
      attachTo: { element: '.onboarding-ingredient-detail', on: 'top' },
      canClickTarget: false,
      buttons: [
        {
          text: t('onboarding.common.previous'),
          action: () => {
            eventBus.emit('onboarding:close-ingredient-sidebar');
            tour.value?.back();
          },
          secondary: true,
        },
        {
          text: t('onboarding.common.next'),
          action: () => {
            eventBus.emit('onboarding:close-ingredient-sidebar');
            tour.value?.next();
          },
        },
      ],
      beforeShowPromise: async () => {
        await waitForElement('.onboarding-ingredient-detail', 10000);
        await nextTick();
      },
    }),
    // 12e - Liste de courses
    createInfoStep({
      id: 'shopping-list',
      title: t('onboarding.chapters.recipeActions.steps.shopping-list.title'),
      text: t('onboarding.chapters.recipeActions.steps.shopping-list.text'),
      attachTo: { element: '#onboarding-add-ingredientList', on: 'top' },
      canClickTarget: false,
      beforeShowPromise: async () => {
        await waitForElement('#onboarding-add-ingredientList', 10000);
        await nextTick();
      },
      buttons: [
        {
          text: t('onboarding.common.next'),
          action: () => {
            eventBus.emit('onboarding:close-ingredient-sidebar');
            tour.value?.next();
          },
        },
      ],
    }),
    //12f - Planifier le plat
    createInfoStep({
      id: 'plan-dish',
      title: t('onboarding.chapters.recipeActions.steps.plan-dish.title'),
      text: t('onboarding.chapters.recipeActions.steps.plan-dish.text'),
      attachTo: { element: '#onboarding-plan-dishes', on: 'top' },
      canClickTarget: false,
      beforeShowPromise: async () => {
        await waitForElement('#onboarding-plan-dishes', 10000);
        await nextTick();
      },
      buttons: [
        {
          text: t('onboarding.common.previous'),
          action: () => {
            eventBus.emit('onboarding:close-ingredient-sidebar');
            tour.value?.back();
          },
          secondary: true,
        },
        {
          text: t('onboarding.common.next'),
          action: () => {
            eventBus.emit('onboarding:close-ingredient-sidebar');
            tour.value?.next();
          },
        },
      ],
    }),
    // 12g — Fiche technique
    createActionStep(
      {
        id: 'generate-tech-sheet',
        title: t('onboarding.chapters.recipeActions.steps.generate-tech-sheet.title'),
        text: t('onboarding.chapters.recipeActions.steps.generate-tech-sheet.text'),
        attachTo: { element: '.btn-generate-sheet', on: 'top' },
        advanceOn: { selector: '.btn-generate-sheet', event: 'click' },
      },
      {
        placement: 'bottom',
      },
    ),
    // // 12f — Valider le dialogue de confirmation de génération de fiche technique
    // createActionStep({
    //   id: 'confirm-tech-sheet-dialog',
    //   title: '✅ Confirmer la génération',
    //   text: 'Validez la génération en cliquant sur "Oui, générer".',
    //   attachTo: { element: "[data-pc-name='confirmpopup']", on: 'top' },
    //   canClickTarget: true,
    //   advanceOn: { selector: '.onboarding-confirm-generation-button', event: 'click' },
    //   buttons: [{ text: 'Précédent', action: () => tour.value?.back(), secondary: true }],
    //   beforeShowPromise: async () => {
    //     // Attend que le dialogue soit visible
    //     await waitForElement("[data-pc-name='confirmpopup']", 10000);
    //     await nextTick();
    //   },
    // }),
    // 13.1 - Modifier la fiche technique
    createInfoStep({
      id: 'edit-tech-sheet',
      title: t('onboarding.chapters.recipeActions.steps.edit-tech-sheet.title'),
      text: t('onboarding.chapters.recipeActions.steps.edit-tech-sheet.text'),
      attachTo: { element: '.onboarding-edit-recipe-button', on: 'bottom' },
      canClickTarget: false,
      beforeShowPromise: async () => {
        await waitForElement('.onboarding-edit-recipe-button', 60000);
        await nextTick();
      },
      buttons: [{ text: t('onboarding.common.next'), action: () => tour.value?.next() }],
    }),
    // 13.2 - Exporter la fiche technique
    createInfoStep({
      id: 'export-tech-sheet',
      title: t('onboarding.chapters.recipeActions.steps.export-tech-sheet.title'),
      text: t('onboarding.chapters.recipeActions.steps.export-tech-sheet.text'),
      attachTo: { element: '.onboarding-export-pdf-button', on: 'bottom' },
      canClickTarget: false,
      beforeShowPromise: async () => {
        await waitForElement('.onboarding-export-pdf-button', 10000);
        await nextTick();
      },
      buttons: [
        { text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true },
        { text: t('onboarding.common.next'), action: () => tour.value?.next() },
      ],
    }),
  ];
}
